import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useMatch, useLocation } from 'react-router-dom';
import DesktopRedirect from './components/routing/DesktopRedirect';
import QueryProvider from './providers/QueryProvider';
import AdminLayout from './components/admin/AdminLayout';
import AdminAuthGuard from './components/admin/AdminAuthGuard';
import RequireCapability from './components/admin/RequireCapability';
import SafeAdminRedirect from './components/admin/SafeAdminRedirect';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import ResetPasswordPage from './pages/admin/ResetPasswordPage';
import SettingsPage from './pages/admin/SettingsPage';
import ActivityPage from './pages/admin/ActivityPage';
import RolesListPage from './pages/admin/RolesListPage';
import RoleDetailPage from './pages/admin/RoleDetailPage';
import AdminApiKeysPage from './pages/admin/AdminApiKeysPage';
import AuditPage from './pages/admin/AuditPage';
import OpsPage from './pages/admin/OpsPage';
import DeployPage from './pages/admin/DeployPage';
import RequireSuperAdmin from './components/admin/RequireSuperAdmin';
import { CapabilityProvider } from './components/admin/CapabilityProvider';

import MobileProperty360Layout from './modules/clc-properties/mobile/MobileProperty360Layout';
const MobileProperty360Hub      = lazy(() => import('./modules/clc-properties/mobile/MobileProperty360Hub'));
const MobileReservations        = lazy(() => import('./modules/clc-properties/mobile/MobileReservations'));
const MobileReservationDetail   = lazy(() => import('./modules/clc-properties/mobile/MobileReservationDetail'));
const MobileOwners              = lazy(() => import('./modules/clc-properties/mobile/MobileOwners'));
const MobileOwnerDetail         = lazy(() => import('./modules/clc-properties/mobile/MobileOwnerDetail'));
const MobilePropertiesHub       = lazy(() => import('./modules/clc-properties/mobile/MobilePropertiesHub'));
const MobilePropertyModule      = lazy(() => import('./modules/clc-properties/mobile/MobilePropertyModule'));
const MobileFinancial           = lazy(() => import('./modules/clc-properties/mobile/MobileFinancial'));
const MobileMasterLedger        = lazy(() => import('./modules/clc-properties/mobile/MobileMasterLedger'));
const AdminAnalyticsModulePage = lazy(() => import('./modules/analytics/AdminAnalyticsModulePage'));
const TaxReviewModulePage = lazy(() => import('./modules/tax-review/TaxReviewModulePage'));
const ClcPropertiesModulePage = lazy(() => import('./modules/clc-properties/ClcPropertiesModulePage'));
const ClcCommunicationsModulePage = lazy(() => import('./modules/clc-communications/ClcCommunicationsModulePage'));
// Compatibility notice for old links; the abandoned Maintenance module has been removed.
const RetiredMaintenancePage = lazy(() => import('./pages/admin/RetiredMaintenancePage'));
const SerenityIntelligenceModulePage = lazy(() => import('./modules/serenity-intelligence/SerenityIntelligenceModulePage'));
const BookingWorkspacesModulePage = lazy(() => import('./modules/booking-workspaces/BookingWorkspacesModulePage'));
const DevelopmentHubModulePage = lazy(() => import('./modules/development-hub/DevelopmentHubModulePage'));
// Public module code downloads while the unchanged access guards run.
const loadDailyDashboardModule = () => import('./modules/daily-dashboard/DailyDashboardModulePage');
const DailyDashboardModulePage = lazy(loadDailyDashboardModule);
const BookingManagerPortalApp = lazy(() => import('./modules/booking-workspaces/BookingManagerPortalApp'));
const OwnerDashboardPage = lazy(() => import('./pages/admin/OwnerDashboardPage'));
const UsersListPage = lazy(() => import('./pages/admin/UsersListPage'));
const UserDetailPage = lazy(() => import('./pages/admin/UserDetailPage'));
const OwnersListPage = lazy(() => import('./pages/admin/OwnersListPage'));
const OwnerDetailPage = lazy(() => import('./pages/admin/OwnerDetailPage'));
const PropertiesListPage = lazy(() => import('./pages/admin/PropertiesListPage'));
const PropertyDetailPage = lazy(() => import('./pages/admin/PropertyDetailPage'));
const NotificationsPage = lazy(() => import('./pages/admin/NotificationsPage'));
const ModulesPage = lazy(() => import('./pages/admin/ModulesPage'));
const APIControllerPage = lazy(() => import('./pages/admin/APIControllerPage'));
const IntegrationDetailPage = lazy(() => import('./pages/admin/IntegrationDetailPage'));
const DocumentsManagerPage = lazy(() => import('./pages/admin/DocumentsManagerPage'));
const DataQualityPage = lazy(() => import('./pages/admin/DataQualityPage'));
const ImportsPage = lazy(() => import('./pages/admin/ImportsPage'));
const ImportRunDetailPage = lazy(() => import('./pages/admin/ImportRunDetailPage'));
const HostawayStatusPage = lazy(() => import('./pages/admin/HostawayStatusPage'));
const HostawayPromotionPage = lazy(() => import('./pages/admin/HostawayPromotionPage'));
const HostawayPropertiesPage = lazy(() => import('./pages/admin/HostawayPropertiesPage'));
const HostawayPropertyReservationsPage = lazy(() => import('./pages/admin/HostawayPropertyReservationsPage'));
const HostawayPropertyDetailPage = lazy(() => import('./pages/admin/HostawayPropertyDetailPage'));
const HostawayReservationsPage = lazy(() => import('./pages/admin/HostawayReservationsPage'));
const CLCReservationsPage = lazy(() => import('./pages/admin/CLCReservationsPage'));
const CLCReservationDetailPage = lazy(() => import('./pages/admin/CLCReservationDetailPage'));
import LegacyTaxReviewRedirect from './components/routing/LegacyTaxReviewRedirect';
const FreshCoveragePage = lazy(() => import('./pages/admin/FreshCoveragePage'));
const FreshPendingPage = lazy(() => import('./pages/admin/FreshPendingPage'));
const EzcarePage = lazy(() => import('./pages/admin/EzcarePage'));
const EzcareJobDetailPage = lazy(() => import('./pages/admin/EzcareJobDetailPage'));
const HostawayReservationDetailPage = lazy(() => import('./pages/admin/HostawayReservationDetailPage'));
const HostawayReviewsPage = lazy(() => import('./pages/admin/HostawayReviewsPage'));
const HostawayReviewDetailPage = lazy(() => import('./pages/admin/HostawayReviewDetailPage'));
const HostawayGuestPaymentChargesPage = lazy(() => import('./pages/admin/HostawayGuestPaymentChargesPage'));
const HostawayAvailabilityPage = lazy(() => import('./pages/admin/HostawayAvailabilityPage'));
const HostawayAvailabilityDetailPage = lazy(() => import('./pages/admin/HostawayAvailabilityDetailPage'));
const HostawayMappingsPage = lazy(() => import('./pages/admin/HostawayMappingsPage'));
import LegacyCommunicationsRoute from './components/routing/LegacyCommunicationsRoute';
import AnalyticsErrorBoundary from './modules/analytics/AnalyticsErrorBoundary';

function ModuleRouteFallback({ label }) {
  return <div style={{ padding: '1rem' }}>Loading {label}…</div>;
}

function LazyModuleRoute({ capability, label, children, errorBoundary: ErrorBoundary = null, preload = null }) {
  useEffect(() => {
    // A failed speculative download must not reject authentication or suppress
    // React.lazy's normal load attempt once access has been granted.
    if (preload) preload().catch(() => {});
  }, [preload]);
  const content = (
    <Suspense fallback={<ModuleRouteFallback label={label} />}>
      {children}
    </Suspense>
  );

  return (
    <AdminAuthGuard>
      <CapabilityProvider>
        <RequireCapability capability={capability}>
          {ErrorBoundary ? <ErrorBoundary>{content}</ErrorBoundary> : content}
        </RequireCapability>
      </CapabilityProvider>
    </AdminAuthGuard>
  );
}

function LazyAdminPageRoute({ capability = null, label, requireSuperAdmin = false, children }) {
  const content = (
    <Suspense fallback={<ModuleRouteFallback label={label} />}>
      {children}
    </Suspense>
  );

  let wrapped = content;
  if (capability) {
    wrapped = <RequireCapability capability={capability}>{wrapped}</RequireCapability>;
  }
  if (requireSuperAdmin) {
    wrapped = <RequireSuperAdmin>{wrapped}</RequireSuperAdmin>;
  }
  return wrapped;
}

/**
 * Intercepts all /modules/clc-properties/* paths on mobile (≤ 480 px) before the desktop
 * module shell renders. Property matches go to /m/properties/:id; everything else (the module hub)
 * goes to /m. On desktop, renders ClcPropertiesModulePage unchanged.
 */
function ClcPropertiesModuleGate() {
  const propertyMatch = useMatch('/modules/clc-properties/properties/:id');
  if (typeof window !== 'undefined' && window.innerWidth <= 480) {
    if (propertyMatch) {
      return <Navigate to={`/m/properties/${propertyMatch.params.id}`} replace />;
    }
    return <Navigate to="/m" replace />;
  }
  return <ClcPropertiesModulePage />;
}

function App() {
  return (
    <QueryProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/booking/workspaces/:slug"
          element={<Suspense fallback={<ModuleRouteFallback label="Booking Manager Portal" />}><BookingManagerPortalApp /></Suspense>}
        />
        <Route
          path="/modules/tax-review/*"
          element={
            <LazyModuleRoute capability="admin:modules:read" label="Tax Review">
              <TaxReviewModulePage />
            </LazyModuleRoute>
          }
        />
        <Route path="/admin/analytics/*" element={<LegacyAnalyticsRedirect />} />
        <Route
          path="/modules/analytics/*"
          element={
            <LazyModuleRoute
              capability="admin:analytics:read"
              label="Analytics"
              errorBoundary={AnalyticsErrorBoundary}
            >
              <AdminAnalyticsModulePage />
            </LazyModuleRoute>
          }
        />
        <Route
          path="/modules/clc-properties/*"
          element={
            <LazyModuleRoute capability="admin:clc-properties:read" label="Property 360">
              <ClcPropertiesModuleGate />
            </LazyModuleRoute>
          }
        />
        <Route
          path="/m"
          element={
            <AdminAuthGuard>
              <CapabilityProvider>
                <RequireCapability capability="admin:clc-properties:read">
                  <Suspense fallback={<ModuleRouteFallback label="Property 360 Mobile" />}>
                    <MobileProperty360Layout />
                  </Suspense>
                </RequireCapability>
              </CapabilityProvider>
            </AdminAuthGuard>
          }
        >
          <Route index                element={<MobileProperty360Hub />} />
          <Route path="reservations"  element={<MobileReservations />} />
          <Route path="reservations/:id" element={<MobileReservationDetail />} />
          <Route path="owners"        element={<MobileOwners />} />
          <Route path="owners/:id"    element={<MobileOwnerDetail />} />
          <Route path="properties"    element={<MobilePropertiesHub />} />
          <Route path="properties/:id/*" element={
            <DesktopRedirect>
              <MobilePropertyModule />
            </DesktopRedirect>
          } />
          <Route path="financial"     element={<MobileFinancial />} />
          <Route path="master-ledger" element={<MobileMasterLedger />} />
        </Route>
        <Route
          path="/modules/clc-communications/*"
          element={
            <LazyModuleRoute capability="admin:clc-communications:read" label="CLC Communications">
              <ClcCommunicationsModulePage />
            </LazyModuleRoute>
          }
        />
        <Route
          path="/modules/clc-maintenance/*"
          element={
            <LazyModuleRoute capability="admin:clc-maintenance:read" label="CLC Maintenance">
              <RetiredMaintenancePage />
            </LazyModuleRoute>
          }
        />
        <Route
          path="/modules/serenity/*"
          element={
            <LazyModuleRoute capability="admin:serenity:read" label="Serenity Intelligence">
              <SerenityIntelligenceModulePage />
            </LazyModuleRoute>
          }
        />
        <Route
          path="/modules/booking-workspaces/*"
          element={
            <LazyModuleRoute capability="admin:workspaces:read" label="Booking Workspaces">
              <BookingWorkspacesModulePage />
            </LazyModuleRoute>
          }
        />
        <Route
          path="/modules/development-hub/*"
          element={
            <LazyModuleRoute capability="admin:development-hub:read" label="Development Hub">
              <DevelopmentHubModulePage />
            </LazyModuleRoute>
          }
        />
        <Route
          path="/modules/daily-dashboard/*"
          element={
            <LazyModuleRoute capability="admin:daily-dashboard:read" label="Daily Dashboard" preload={loadDailyDashboardModule}>
              <DailyDashboardModulePage />
            </LazyModuleRoute>
          }
        />
        <Route path="/admin" element={<AdminAuthGuard><AdminLayout /></AdminAuthGuard>}>
          <Route
            index
            element={<SafeAdminRedirect />}
          />
          <Route
            path="owner-dashboard"
            element={
              <LazyAdminPageRoute capability="admin:owner-dashboard:read" label="Owner Dashboard">
                <OwnerDashboardPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="api-controller"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="API Controller">
                <APIControllerPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="integrations/:code"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Integration Detail">
                <IntegrationDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route path="settings" element={<SettingsPage />} />
          <Route
            path="activity"
            element={
              <RequireCapability capability="admin:audit:read">
                <ActivityPage />
              </RequireCapability>
            }
          />
          <Route
            path="audit"
            element={
              <RequireCapability capability="admin:audit:read">
                <AuditPage />
              </RequireCapability>
            }
          />
          <Route
            path="users"
            element={
              <LazyAdminPageRoute capability="admin:rbac:read" label="Users">
                <UsersListPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="users/:id"
            element={
              <LazyAdminPageRoute capability="admin:rbac:read" label="User Detail">
                <UserDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="roles"
            element={
              <RequireCapability capability="admin:rbac:read">
                <RolesListPage />
              </RequireCapability>
            }
          />
          <Route
            path="roles/:id"
            element={
              <RequireCapability capability="admin:rbac:read">
                <RoleDetailPage />
              </RequireCapability>
            }
          />
          <Route
            path="api-keys"
            element={
              <RequireCapability capability="admin:api-keys:read">
                <AdminApiKeysPage />
              </RequireCapability>
            }
          />
          <Route
            path="ops"
            element={<OpsPage />}
          />
          <Route
            path="deploy"
            element={
              <LazyAdminPageRoute label="Deploy" requireSuperAdmin>
                <DeployPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="modules"
            element={<LazyAdminPageRoute label="Modules"><ModulesPage /></LazyAdminPageRoute>}
          />
          <Route
            path="notifications"
            element={<LazyAdminPageRoute label="Notifications"><NotificationsPage /></LazyAdminPageRoute>}
          />
          <Route
            path="owners"
            element={
              <LazyAdminPageRoute capability="admin:owner-dashboard:read" label="Owners">
                <OwnersListPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="owners/:id"
            element={
              <LazyAdminPageRoute capability="admin:owner-dashboard:read" label="Owner Detail">
                <OwnerDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="properties"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Properties">
                <PropertiesListPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="properties/:id"
            element={
              <LazyAdminPageRoute capability="admin:owner-dashboard:read" label="Property Detail">
                <PropertyDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="link-manager"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Documents Manager">
                <DocumentsManagerPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="data-quality"
            element={
              <LazyAdminPageRoute capability="admin:owner-dashboard:read" label="Data Quality">
                <DataQualityPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="imports"
            element={
              <LazyAdminPageRoute capability="admin:owner-dashboard:read" label="Imports">
                <ImportsPage />
              </LazyAdminPageRoute>
            }
          />
          {/* Hostaway */}
          <Route
            path="hostaway/status"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Status">
                <HostawayStatusPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/promotion"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Promotion">
                <HostawayPromotionPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/properties"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Properties">
                <HostawayPropertiesPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/properties/:listingId/reservations"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Property Reservations">
                <HostawayPropertyReservationsPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/properties/:listingId"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Property Detail">
                <HostawayPropertyDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/reservations"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Reservations">
                <HostawayReservationsPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="clc-reservations"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="CLC Reservations">
                <CLCReservationsPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="clc-reservations/:reservationId"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="CLC Reservation Detail">
                <CLCReservationDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="tax-review"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Tax Review">
                <LegacyTaxReviewRedirect />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="financials/fresh-coverage"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Fresh Coverage">
                <FreshCoveragePage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="financials/fresh-pending"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Fresh Pending">
                <FreshPendingPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="ezcare"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="EZCare">
                <EzcarePage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="ezcare/jobs/:ez_job_id"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="EZCare Job Detail">
                <EzcareJobDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/reservations/:reservationId"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Reservation Detail">
                <HostawayReservationDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/reviews"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Reviews">
                <HostawayReviewsPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/reviews/:review_id"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Review Detail">
                <HostawayReviewDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/guest-payment-charges"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Guest Payment Charges">
                <HostawayGuestPaymentChargesPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/availability"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Availability">
                <HostawayAvailabilityPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/availability/:hostawayListingId/:date"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Availability Detail">
                <HostawayAvailabilityDetailPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="hostaway/mappings"
            element={
              <LazyAdminPageRoute capability="admin:integrations:read" label="Hostaway Mappings">
                <HostawayMappingsPage />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="imports/runs/:id"
            element={
              <LazyAdminPageRoute capability="admin:owner-dashboard:read" label="Import Run Detail">
                <ImportRunDetailPage />
              </LazyAdminPageRoute>
            }
          />
          {/* Communications routes */}
          <Route
            path="communications"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communications">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/types"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communication Types">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/types/:typeKey"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communication Types">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/fields"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communication Fields">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/fields/:fieldKey"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communication Fields">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/validate"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communication Validation">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/templates"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communication Templates">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/templates/new"
            element={
              <LazyAdminPageRoute capability="admin:communications:write" label="New Communication Template">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/templates/:id"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communication Template">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/templates/:id/edit"
            element={
              <LazyAdminPageRoute capability="admin:communications:write" label="Edit Communication Template">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/templates/:id/preview"
            element={
              <LazyAdminPageRoute capability="admin:communications:read" label="Communication Preview">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/history"
            element={
              <LazyAdminPageRoute capability="admin:communications:audit" label="Communication History">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/history/:id"
            element={
              <LazyAdminPageRoute capability="admin:communications:audit" label="Communication History Detail">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/inbox"
            element={
              <LazyAdminPageRoute capability="admin:communications:inbox:read" label="Communications Inbox">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
          <Route
            path="communications/inbox/:id"
            element={
              <LazyAdminPageRoute capability="admin:communications:inbox:read" label="Communication Thread">
                <LegacyCommunicationsRoute />
              </LazyAdminPageRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
    </QueryProvider>
  );
}

export default App;

// Preserve bookmarked report state while canonical routes enforce existing access.
function LegacyAnalyticsRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate replace to={{
    pathname: `/modules/analytics${pathname.slice('/admin/analytics'.length)}`,
    search,
    hash,
  }} />;
}
